import React, { useEffect, useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { useDispatch, useSelector } from 'react-redux';
import { set } from 'lodash';
import { Row, Col, FormGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { MultiSelect } from 'react-multi-select-component';
import { createOptions } from '../../Components/helper';
import * as action from './store/Document.action';
import CustomTooltip from '../../Components/Tooltip/CustomTooltip';
import * as systemMasterAction from '../SystemMasters/Store/SystemMasters.action';
import * as bomLibAction from '../Library/BomLibrary/store/BomLibrary.action';
import { useManualState } from './Provider';
import { ClearIcon, SaveIcon, FilterIcon } from '../../bombiix-icon';

function DocumentFilter() {
  const { state, setState, isSupplierLoggedIn } = useManualState();
  const { selectedFilterType, selectedFilterBrand, selectedFilterSuppliers, selectedFilterSubBrand, isFilter, isSubBrandAccess } = state;
  const dispatch = useDispatch();
  const reducer = useSelector((redState) => redState);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [manualTypeOptions, setManualTypeOptions] = useState([]);
  const [subBrandOptions, setSubBrandOptions] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const {
    UserDetails: { userInfo },
    SystemMastersDetails: { brandList, documentTypeList, multiSubBrandList },
    BomLibraryDetails: { ReferenceSupplier },
  } = reducer;

  const [show, setShow] = useState(false);

  const handleDateChange = (dates) => {
    if (Array.isArray(dates)) {
      const [start, end] = dates;
      setStartDate(start);
      setEndDate(end);
    } else {
      setStartDate(dates);
      setEndDate(null);
    }
  };

  useEffect(() => {
    const callApi = async () => {
      await dispatch(
        systemMasterAction.getBrandDetails({
          loggedInUserId: userInfo?.userId,
          isActive: 1,
        }),
      );
      await dispatch(
        systemMasterAction.fetchManualDocumentType({
          isActive: 1,
        }),
      );
      await dispatch(bomLibAction.fetchBomReferenceSupplier());
    };

    callApi();
  }, []);

  // Fetch sub-brands when selected brands change
  useEffect(() => {
    if (selectedFilterBrand?.length > 0 && isSubBrandAccess) {
      dispatch(systemMasterAction.getMultiSubBrandDetails({ brandId: selectedFilterBrand }));
    } else {
      setSubBrandOptions([]);
    }
  }, [dispatch, selectedFilterBrand]);

  // Update sub brand options when multiSubBrandList changes
  useEffect(() => {
    if (multiSubBrandList?.length && isSubBrandAccess) {
      setSubBrandOptions(createOptions(multiSubBrandList, 'name', '_id'));
    } else {
      setSubBrandOptions([]);
    }
  }, [multiSubBrandList]);

  const handleShow = () => {
    setShow(!show);
  };

  const { t } = useTranslation('common');

  useEffect(() => {
    if (ReferenceSupplier) {
      if (isSupplierLoggedIn) {
        const options = ReferenceSupplier.filter((item) => item?._id === userInfo?.userId);
        setSupplierOptions(createOptions(options, 'name', '_id'));
      } else {
        setSupplierOptions(createOptions(ReferenceSupplier, 'name', '_id'));
      }
    }
  }, [ReferenceSupplier, userInfo, isSupplierLoggedIn]);

  useEffect(() => {
    setBrandOptions(createOptions(brandList, 'name', '_id'));
    setManualTypeOptions(createOptions(documentTypeList, 'name', '_id'));
  }, [brandList, documentTypeList]);

  const handleSelectChange = async (value, keyName) => {
    const copyState = state;

    set(copyState, keyName, value);

    setState((prevState) => ({ ...prevState, ...copyState }));
  };

  const handleFilterData = async () => {
    setShow(false);
    const filterObj = {
      suppliers: state?.selectedFilterSuppliers,
      documentFilterType: state?.selectedFilterType,
      brand: state?.selectedFilterBrand,
      subBrand: state?.selectedFilterSubBrand,
      startDate,
      endDate,
    };
    if (
      state?.selectedFilterSuppliers.length > 0 ||
      state?.selectedFilterType?.length > 0 ||
      state?.selectedFilterBrand?.length > 0 ||
      state?.selectedFilterSubBrand?.length > 0 ||
      state?.startDate ||
      state?.endDate
    ) {
      localStorage.setItem('documentsFilter', JSON.stringify(filterObj));
      setState((prev) => ({
        ...prev,
        isFilter: true,
      }));
    }

    dispatch(action.clearReducer());
    const documentData = JSON.parse(localStorage.getItem('manualDoumentFolder')) || {};
    let folderId = null;
    if (documentData?._id) {
      folderId = documentData?._id;
      await dispatch(action.getBreadCrumbDocumentData(documentData?._id));
    }
    const reqObj = {
      pageIndex: 1,
      pageSize: state?.pageSize,
      documentType: state?.selectedFilterType,
      brand: state?.selectedFilterBrand,
      subBrand: state?.selectedFilterSubBrand,
      suppliers: state?.selectedFilterSuppliers,
      startDate: startDate ? startDate.toISOString() : null,
      endDate: endDate ? endDate.toISOString() : null,
      folderId,
      isArchived: state?.isArchived,
    };
    setState((prev) => ({
      ...prev,
      loading: true,
      documentList: [],
      pageIndex: 1,
    }));

    const res = await dispatch(action.getManualDocumentList(reqObj));
    if (res) {
      setState((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  };

  const handleClearData = async () => {
    // Prevent duplicate actions if loading is already true
    if (state.loading) return;

    setState((prev) => ({
      ...prev,
      loading: true, // Set loading to true immediately to block further clicks
      documentList: [],
      selectedFilterType: [],
      selectedFilterBrand: [],
      selectedFilterSuppliers: [],
      selectedFilterSubBrand: [],
      startDate: null,
      endDate: null,
      isFilter: false,
    }));

    // Clear localStorage
    if (localStorage.getItem('documentsFilter')) {
      localStorage.removeItem('documentsFilter');
    }

    setStartDate(null);
    setEndDate(null);
    dispatch(action.clearReducer());

    const documentData = JSON.parse(localStorage.getItem('manualDoumentFolder')) || {};
    let folderId = null;

    if (documentData?._id) {
      folderId = documentData?._id;
      await dispatch(action.getBreadCrumbDocumentData(documentData?._id));
    }

    const reqObj = {
      pageIndex: 1,
      pageSize: state?.pageSize,
      folderId,
    };

    try {
      const res = await dispatch(action.getManualDocumentList(reqObj));
      if (res) {
        setState((prev) => ({
          ...prev,
          loading: false, // Set loading to false once the action is complete
        }));
      }
    } catch (error) {
      console.error('Error fetching document list:', error);
      setState((prev) => ({
        ...prev,
        loading: false, // Ensure loading is reset even if there's an error
      }));
    }
  };

  const resetFilters = () => {
    setShow(false);
    handleClearData();
  };

  return (
    <div className="modified-popOver bom_filter_btn">
      <CustomTooltip placement="top" tooltipText={t('COMPONENET_FILTER.FILTER')}>
        <button
          type="button"
          onClick={handleShow}
          aria-label="filter"
          className={isFilter ? 'btn p-0 mr-16 d-inline-flex ml-8 pr-0 active' : 'btn p-0 mr-16 ml-8 d-inline-flex pr-0'}
        >
          <OverlayTrigger
            placement={t('COMPONENET_FILTER.BUTTON')}
            trigger="click"
            rootClose
            overlay={
              <Popover className="custom-popover document-filter">
                <Popover.Content>
                  <Row className="form-row">
                    <Col md={6}>
                      <FormGroup>
                        <MultiSelect
                          name="group"
                          options={manualTypeOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          overrideStrings={{
                            selectSomeItems: t('FILTER.SELECT_TYPE'),
                          }}
                          value={manualTypeOptions?.filter((val) => selectedFilterType?.includes(val?._id))}
                          labelledBy="select type"
                          onChange={(selected) =>
                            handleSelectChange(
                              selected.map((val) => val?._id),
                              'selectedFilterType',
                            )
                          }
                        />
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup>
                        <MultiSelect
                          name="group"
                          options={brandOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          overrideStrings={{
                            selectSomeItems: 'Select Brand',
                          }}
                          value={brandOptions?.filter((val) => selectedFilterBrand?.includes(val?._id))}
                          labelledBy="Brand Not selected"
                          onChange={(selected) =>
                            handleSelectChange(
                              selected.map((val) => val?._id),
                              'selectedFilterBrand',
                            )
                          }
                        />
                      </FormGroup>
                    </Col>

                    {isSubBrandAccess && (
                      <Col md={6}>
                        <FormGroup>
                          <MultiSelect
                            name="subBrand"
                            options={subBrandOptions}
                            className="product-custom-multiselect common-new-multiselect w-100"
                            overrideStrings={{
                              selectSomeItems: 'Select Sub Brand',
                            }}
                            value={subBrandOptions?.filter((val) => selectedFilterSubBrand?.includes(val?._id))}
                            labelledBy="Sub Brand Not selected"
                            onChange={(selected) =>
                              handleSelectChange(
                                selected.map((val) => val?._id),
                                'selectedFilterSubBrand',
                              )
                            }
                          />
                        </FormGroup>
                      </Col>
                    )}

                    <Col md={6}>
                      <FormGroup>
                        <MultiSelect
                          name="group"
                          options={supplierOptions}
                          className="product-custom-multiselect common-new-multiselect w-100"
                          overrideStrings={{
                            selectSomeItems: t('FILTER.SELECT_SUPPLIER'),
                          }}
                          value={supplierOptions?.filter((val) => selectedFilterSuppliers?.includes(val?._id))}
                          labelledBy={t('FILTER.SELECT_SUPPLIER')}
                          onChange={(selected) =>
                            handleSelectChange(
                              selected.map((val) => val?._id),
                              'selectedFilterSuppliers',
                            )
                          }
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <DatePicker
                          selected={startDate}
                          onChange={handleDateChange}
                          startDate={startDate}
                          endDate={endDate}
                          selectsRange
                          placeholderText="Select Expiry Date"
                          dateFormat="dd-MM-yyyy"
                          className="form-control manual-datepicker"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <div className=" d-flex justify-content-end">
                        <div className="">
                          <CustomTooltip placement="top" tooltipText={t('COMPONENET_FILTER.CLEAR')}>
                            <button aria-label="clear" type="button" onClick={resetFilters} id="Clear" className="btn p-0" disabled={state.loading}>
                              <ClearIcon size={28} />
                            </button>
                          </CustomTooltip>
                          <CustomTooltip placement="top" tooltipText={t('COMPONENET_FILTER.APPLY')}>
                            <button aria-label="apply" type="submit" onClick={handleFilterData} id="Apply" className="btn ml-16 p-0">
                              <SaveIcon size={28} />
                            </button>
                          </CustomTooltip>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Popover.Content>
              </Popover>
            }
          >
            <span className="d-inline-flex"><FilterIcon size={28} /></span>
          </OverlayTrigger>
        </button>
      </CustomTooltip>
    </div>
  );
}

export default DocumentFilter;
